import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import React, { useContext, useEffect, useState } from "react";
import { Message, MessageAttachment } from "schemas/email";

import EmailThread from "components/EmailThread";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { openBase64DataInTab } from "utils/files";
import { titleCase } from "utils/string";
import { formatEpochTimestamp } from "utils/time";

import styles from "./styles";

interface WinPartnership {
  id: number;
  organizationId: number;
  gmailThreadId: string | null;
  brandName: string | null;
  status: string | null;
  createdAt: number;
  cashValueAmount: number | null;
  cashCurrency: string | null;
  isGifted: boolean;
  userTypes: string[];
}

interface CurrencyTotal {
  currency: string;
  totalAmount: number;
}

interface WinsStats {
  newPartnershipsCount: number;
  usersLandingPartnershipsCount: number;
  topCashTotals: CurrencyTotal[];
  otherCurrencyCount: number;
}

interface WinsDashboardResponse {
  windowDays: number;
  stats: WinsStats;
  recentPartnerships: WinPartnership[];
}

const formatCashAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString("en-US")} ${currency}`;
  }
};

const formatCompensation = (p: WinPartnership) => {
  if (p.cashValueAmount && p.cashCurrency) {
    return `${formatCashAmount(p.cashValueAmount, p.cashCurrency)} ${
      p.cashCurrency
    }`;
  }
  if (p.isGifted) return "Gifted";
  return "—";
};

const WinsDashboard: React.FC = () => {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WinsDashboardResponse | null>(null);
  const [threadPartnership, setThreadPartnership] =
    useState<WinPartnership | null>(null);
  const [threadMessages, setThreadMessages] = useState<Message[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  const openThread = async (p: WinPartnership) => {
    if (!p.gmailThreadId) return;
    setThreadPartnership(p);
    setThreadMessages([]);
    setThreadLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${p.organizationId}/threads/${p.gmailThreadId}`,
        "GET",
      );
      setThreadMessages(res.messages);
    } catch (error) {
      setAlert(`Failed to load email thread: ${error}`, "warning");
    } finally {
      setThreadLoading(false);
    }
  };

  const closeThread = () => {
    setThreadPartnership(null);
    setThreadMessages([]);
  };

  const openThreadAttachment = async (
    messageId: string,
    attachment: MessageAttachment,
  ) => {
    if (!threadPartnership) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${threadPartnership.organizationId}/messages/${messageId}/attachments/${attachment.attachment_id}`,
        "GET",
      );
      openBase64DataInTab(res.attachment.data, attachment.mime_type);
    } catch (error) {
      setAlert(`Failed to load attachment: ${error}`, "warning");
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/wins-dashboard/stats",
        "GET",
      );
      setData(res);
    } catch (error) {
      setAlert("Failed to load wins dashboard", "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windowDays = data?.windowDays ?? 14;

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2 }}>
          <Backdrop sx={styles.backdrop} open={loading}>
            <CircularProgress />
          </Backdrop>

          <Typography variant="h3" gutterBottom>
            <strong>Wins Dashboard</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            Bento-sourced partnerships landed (past “in discussion”) in the last{" "}
            {windowDays} days.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={styles.statCard}>
                <CardContent>
                  <Typography variant="overline" sx={styles.statLabel}>
                    New partnerships landed
                  </Typography>
                  <Typography variant="h3" sx={styles.statValue}>
                    {data?.stats.newPartnershipsCount ?? "—"}
                  </Typography>
                  <Typography variant="caption" sx={styles.statHint}>
                    Past “in discussion”, last {windowDays} days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={styles.statCard}>
                <CardContent>
                  <Typography variant="overline" sx={styles.statLabel}>
                    Users landing partnerships
                  </Typography>
                  <Typography variant="h3" sx={styles.statValue}>
                    {data?.stats.usersLandingPartnershipsCount ?? "—"}
                  </Typography>
                  <Typography variant="caption" sx={styles.statHint}>
                    Distinct organizations with a win
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={styles.statCard}>
                <CardContent>
                  <Typography variant="overline" sx={styles.statLabel}>
                    Cash compensation
                  </Typography>
                  {!data || data.stats.topCashTotals.length === 0 ? (
                    <Typography variant="h3" sx={styles.statValue}>
                      —
                    </Typography>
                  ) : (
                    <>
                      {data.stats.topCashTotals.map((t) => (
                        <Typography
                          key={t.currency}
                          variant="h5"
                          sx={styles.currencyLine}
                        >
                          {formatCashAmount(t.totalAmount, t.currency)}{" "}
                          <Typography
                            component="span"
                            variant="body2"
                            sx={styles.currencyCode}
                          >
                            {t.currency}
                          </Typography>
                        </Typography>
                      ))}
                      {data.stats.otherCurrencyCount > 0 && (
                        <Typography variant="caption" sx={styles.statHint}>
                          + {data.stats.otherCurrencyCount} more{" "}
                          {data.stats.otherCurrencyCount === 1
                            ? "currency"
                            : "currencies"}
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h5" gutterBottom>
            <strong>Recent wins</strong>
          </Typography>
          <TableContainer sx={styles.tableContainer}>
            <Table stickyHeader sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Brand</TableCell>
                  <TableCell>User type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cash</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Thread</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.recentPartnerships.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.brandName || "—"}</TableCell>
                    <TableCell>
                      {p.userTypes.length > 0 ? p.userTypes.join(", ") : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.status ? titleCase(p.status) : "—"}
                        sx={styles.statusChip}
                      />
                    </TableCell>
                    <TableCell>{formatCompensation(p)}</TableCell>
                    <TableCell>
                      {formatEpochTimestamp({ epochTimestamp: p.createdAt })}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={!p.gmailThreadId}
                        onClick={() => openThread(p)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && data?.recentPartnerships.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No wins in the last {windowDays} days yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={threadPartnership !== null}
            onClose={closeThread}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle sx={styles.threadDialogTitle}>
              <Box>
                <Typography variant="h6">
                  {threadPartnership?.brandName || "Thread"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Org #{threadPartnership?.organizationId} ·{" "}
                  {threadPartnership?.gmailThreadId}
                </Typography>
              </Box>
              <Button onClick={closeThread} size="small">
                Close
              </Button>
            </DialogTitle>
            <DialogContent dividers sx={styles.threadDialogContent}>
              {threadLoading ? (
                <Box sx={styles.threadLoadingWrapper}>
                  <CircularProgress />
                </Box>
              ) : (
                <EmailThread
                  messages={threadMessages}
                  openAttachment={openThreadAttachment}
                />
              )}
            </DialogContent>
          </Dialog>
        </Grid>
      }
    />
  );
};

export default WinsDashboard;
